/**
 * Hooks for case scope management
 *
 * Provides hooks for the living document workspace including
 * scope documents, versions, and notes.
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  CaseScope,
  CaseScopeVersion,
  CaseScopeNote,
  UpdateScopeRequest,
  CreateScopeNoteRequest,
  UpdateScopeNoteRequest,
  ListVersionsQuery,
  ListNotesQuery,
} from '../types/case-container'
import {
  getScope,
  updateScope,
  listVersions,
  restoreVersion,
  listNotes,
  createNote,
  updateNote,
  deleteNote,
} from '../services/case-scope'

// =============================================================================
// useCaseScope - Fetch scope document
// =============================================================================

interface UseCaseScopeResult {
  scope: CaseScope | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the scope document for a case
 */
export function useCaseScope(caseId: string | null): UseCaseScopeResult {
  const [scope, setScope] = useState<CaseScope | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setScope(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getScope(caseId)
      setScope(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch scope')
      setScope(null)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { scope, loading, error, refetch: fetch }
}

// =============================================================================
// useScopeVersions - Fetch version history
// =============================================================================

interface UseScopeVersionsResult {
  versions: CaseScopeVersion[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch scope version history
 */
export function useScopeVersions(
  caseId: string | null,
  params?: ListVersionsQuery
): UseScopeVersionsResult {
  const [versions, setVersions] = useState<CaseScopeVersion[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const versionsLimit = params?.limit
  const versionsOffset = params?.offset

  const fetch = useCallback(async () => {
    if (!caseId) {
      setVersions([])
      setTotal(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listVersions(caseId, params)
      setVersions(data.versions)
      setTotal(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch versions')
      setVersions([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, versionsLimit, versionsOffset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { versions, total, loading, error, refetch: fetch }
}

// =============================================================================
// useScopeNotes - Fetch notes
// =============================================================================

interface UseScopeNotesResult {
  notes: CaseScopeNote[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch scope notes
 */
export function useScopeNotes(caseId: string | null, params?: ListNotesQuery): UseScopeNotesResult {
  const [notes, setNotes] = useState<CaseScopeNote[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const tag = params?.tag
  const notesLimit = params?.limit
  const notesOffset = params?.offset

  const fetch = useCallback(async () => {
    if (!caseId) {
      setNotes([])
      setTotal(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listNotes(caseId, params)
      setNotes(data.notes)
      setTotal(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notes')
      setNotes([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, tag, notesLimit, notesOffset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { notes, total, loading, error, refetch: fetch }
}

// =============================================================================
// useScopeMutations - Scope document mutations
// =============================================================================

interface UseScopeMutationsResult {
  saving: boolean
  error: string | null
  update: (caseId: string, data: UpdateScopeRequest) => Promise<CaseScope | null>
  restore: (caseId: string, version: number) => Promise<CaseScope | null>
  clearError: () => void
}

/**
 * Hook for scope document mutations
 */
export function useScopeMutations(): UseScopeMutationsResult {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = useCallback(
    async (caseId: string, data: UpdateScopeRequest): Promise<CaseScope | null> => {
      setSaving(true)
      setError(null)

      try {
        const result = await updateScope(caseId, data)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update scope')
        return null
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const restore = useCallback(
    async (caseId: string, version: number): Promise<CaseScope | null> => {
      setSaving(true)
      setError(null)

      try {
        const result = await restoreVersion(caseId, version)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to restore version')
        return null
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { saving, error, update, restore, clearError }
}

// =============================================================================
// useNoteMutations - Note mutations
// =============================================================================

interface UseNoteMutationsResult {
  saving: boolean
  error: string | null
  create: (caseId: string, data: CreateScopeNoteRequest) => Promise<CaseScopeNote | null>
  update: (
    caseId: string,
    noteId: string,
    data: UpdateScopeNoteRequest
  ) => Promise<CaseScopeNote | null>
  remove: (caseId: string, noteId: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for note mutations
 */
export function useNoteMutations(): UseNoteMutationsResult {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    async (caseId: string, data: CreateScopeNoteRequest): Promise<CaseScopeNote | null> => {
      setSaving(true)
      setError(null)

      try {
        const result = await createNote(caseId, data)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create note')
        return null
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const update = useCallback(
    async (
      caseId: string,
      noteId: string,
      data: UpdateScopeNoteRequest
    ): Promise<CaseScopeNote | null> => {
      setSaving(true)
      setError(null)

      try {
        const result = await updateNote(caseId, noteId, data)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update note')
        return null
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const remove = useCallback(async (caseId: string, noteId: string): Promise<boolean> => {
    setSaving(true)
    setError(null)

    try {
      await deleteNote(caseId, noteId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note')
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { saving, error, create, update, remove, clearError }
}
